// CoStreamRequestModal — TikTok-style "Request to Co-Stream" flow.
// Any logged-in viewer (except the stream's creator) can open it and send a
// request. Nothing happens on-air until the streamer accepts from CohostInbox.
// No auto-join, no auto-publish. Legacy component name kept for import back-compat.
import { useState } from "react";
import { UserPlus, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";

export default function StreamTakeoverModal({ streamId, streamCreatorId, open, onClose, onRequested }) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Skip entirely if the viewer IS the stream creator, or if not logged in, or closed.
  if (!open) return null;
  if (!user) return null;
  if (streamCreatorId && user.id === streamCreatorId) return null;

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      await api.post("/cohost/request", { stream_id: streamId, message: message.trim() || "Request to co-stream" });
      toast.success("Request sent — waiting for the streamer to accept");
      onRequested?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="cohost-request-modal">
      <div className="bg-nexus-surface border border-nexus-magenta/40 max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-nexus-magenta">// request to co-stream</div>
            <h3 className="font-display font-black text-2xl mt-1">Ask to join the stream.</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="cohost-request-close"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-xs text-white/60 leading-relaxed">
          Your camera and mic will <strong className="text-white">not</strong> turn on until the streamer accepts.
          Add a short note so they know why you want to join.
        </p>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optional note (e.g. 'shoutout my new drop', 'react to my beat')"
          maxLength={280}
          className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm min-h-[80px]"
          data-testid="cohost-request-message"
        />

        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="cohost-request-cancel">Cancel</button>
          <button
            onClick={send}
            disabled={sending}
            className="btn-nexus flex-1 justify-center bg-nexus-magenta text-white hover:opacity-90 disabled:opacity-50"
            data-testid="cohost-request-send"
          >
            <UserPlus className="w-4 h-4" /> {sending ? "Sending…" : "Send request"}
          </button>
        </div>

        <div className="text-[10px] text-white/40 leading-relaxed border-t border-white/10 pt-3">
          Cancel anytime by refreshing the page. Only one pending request per stream is kept.
        </div>
      </div>
    </div>
  );
}
